import * as THREE from 'three';
import type { InputState } from './InputHandler';
import { DRONE_BASE_SPEED, FIGHTER_BASE_SPEED, DRONE_BOOST_MULTIPLIER } from '@shared/constants';
import type { Role } from '@shared/types';

// ============================================================
// Arcade flight controller
//
// W = pitch down (dive)
// S = pitch up (climb)
// A = turn left  (yaw + auto-bank)
// D = turn right (yaw + auto-bank)
// Q = roll counter-clockwise (longitudinal axis)
// E = roll clockwise (longitudinal axis)
// Mouse X = fine yaw (after pointer lock)
// Mouse Y = fine pitch
// ============================================================

const DRONE_PITCH_RATE = 0.9;
const DRONE_YAW_RATE = 0.9;
const F16_PITCH_RATE = 1.4;
const F16_YAW_RATE = 1.4;
const BANK_ANGLE = 0.6;
const INPUT_RAMP_SPEED = 6.0; // how fast input ramps up/down (higher = snappier)
const BANK_SPEED = 4.0;
const ROLL_DURATION = 0.5; // seconds for a full 360°
const ROLL_SPEED = (2 * Math.PI) / ROLL_DURATION;
const ROLL_LATERAL_DISTANCE = 40; // how far to shift sideways during a roll
const AUTO_LEVEL_SPEED = 3.0; // how fast the aircraft levels out
const BRAKE_MULTIPLIER = 0.4;  // speed multiplier when braking
const BOOST_MAX_DURATION = 5; // seconds of boost fuel
const BOOST_REFILL_DELAY = 3; // seconds before fuel starts refilling
const BOOST_REFILL_RATE = 2.5; // seconds to fully refill (fuel per second = max/rate)
const GUN_MAX_HEAT = 5;        // seconds of continuous fire before overheat
const GUN_COOLDOWN_DELAY = 1.5; // seconds before gun starts cooling
const GUN_COOL_RATE = 5;        // seconds to fully cool down
const MIN_ALTITUDE = 5;
const MOUSE_SENSITIVITY = 0.8;  // how much mouse movement contributes to steering

// Reusable objects
const _forward = new THREE.Vector3();
const _euler = new THREE.Euler();
const _qPitch = new THREE.Quaternion();
const _qYaw = new THREE.Quaternion();
const _qRoll = new THREE.Quaternion();
const _axisRight = new THREE.Vector3(1, 0, 0);
const _axisUp = new THREE.Vector3(0, 1, 0);
const _axisForward = new THREE.Vector3(0, 0, -1);
const _qBank = new THREE.Quaternion();
const _bankAxis = new THREE.Vector3(0, 0, 1);

export class FlightController {
  readonly mesh: THREE.Group;
  private speed: number;
  private readonly baseSpeed: number;
  private readonly boostSpeed: number;
  private visualBank = 0;
  private rollRemaining = 0;
  private rollDir = 0;
  private rollCooldown = 0;
  private readonly rollRight = new THREE.Vector3();
  private boostFuel = BOOST_MAX_DURATION;
  private boostRefillTimer = 0;
  private boosting = false;
  private gunHeat = 0;
  private gunCooldownTimer = 0;
  private firing = false;
  private readonly role: Role;
  private readonly pitchRate: number;
  private flareUses = 3;
  private flareCooldownTimer = 0;
  private flareJustFired = false;
  private rollJustStarted = false;
  private readonly yawRate: number;
  private currentPitch = 0;
  private currentYaw = 0;
  private readonly _velocity = new THREE.Vector3();

  constructor(mesh: THREE.Group, role: Role) {
    this.role = role;
    this.pitchRate = role === 'f16' ? F16_PITCH_RATE : DRONE_PITCH_RATE;
    this.yawRate = role === 'f16' ? F16_YAW_RATE : DRONE_YAW_RATE;
    this.mesh = mesh;
    this.baseSpeed = role === 'shahed' ? DRONE_BASE_SPEED : FIGHTER_BASE_SPEED;
    this.boostSpeed = role === 'shahed'
      ? DRONE_BASE_SPEED * DRONE_BOOST_MULTIPLIER
      : FIGHTER_BASE_SPEED;
    this.speed = this.baseSpeed;
  }

  update(input: InputState, dt: number): void {
    // ── Boost (drone only) ────────────────────────────────
    if (this.role === 'shahed') {
      if (input.up && this.boostFuel > 0) {
        this.boosting = true;
        this.boostFuel = Math.max(0, this.boostFuel - dt);
        this.boostRefillTimer = BOOST_REFILL_DELAY;
      } else {
        this.boosting = false;
        this.boostRefillTimer = Math.max(0, this.boostRefillTimer - dt);
        if (this.boostRefillTimer <= 0) {
          this.boostFuel = Math.min(BOOST_MAX_DURATION, this.boostFuel + (BOOST_MAX_DURATION / BOOST_REFILL_RATE) * dt);
        }
      }
    }

    // ── Gun (F-16 only, disabled in lock mode) ────────────
    if (this.role === 'f16') {
      if (input.up && !input.lockMode && this.gunHeat < GUN_MAX_HEAT) {
        this.firing = true;
        this.gunHeat = Math.min(GUN_MAX_HEAT, this.gunHeat + dt);
        this.gunCooldownTimer = GUN_COOLDOWN_DELAY;
      } else {
        this.firing = false;
        this.gunCooldownTimer = Math.max(0, this.gunCooldownTimer - dt);
        if (this.gunCooldownTimer <= 0) {
          this.gunHeat = Math.max(0, this.gunHeat - (GUN_MAX_HEAT / GUN_COOL_RATE) * dt);
        }
      }
    }

    // ── Speed ─────────────────────────────────────────────
    let targetSpeed = this.boosting ? this.boostSpeed : this.baseSpeed;
    if (input.down) targetSpeed = this.baseSpeed * BRAKE_MULTIPLIER;
    this.speed += (targetSpeed - this.speed) * 5 * dt;

    // ── Strip visual bank
    const prevBank = this.visualBank;
    _qBank.setFromAxisAngle(_bankAxis, -prevBank);
    this.mesh.quaternion.multiply(_qBank);
    this.mesh.quaternion.normalize();

    // Pitch (ramped keyboard + mouse)
    let targetPitch = 0;
    if (input.backward) targetPitch += 1;
    if (input.forward) targetPitch -= 1;
    targetPitch -= input.mouseY * MOUSE_SENSITIVITY;
    targetPitch = Math.max(-1, Math.min(1, targetPitch));
    const pitchRamp = 1 - Math.exp(-INPUT_RAMP_SPEED * dt);
    this.currentPitch += (targetPitch - this.currentPitch) * pitchRamp;

    _qPitch.setFromAxisAngle(_axisRight, this.currentPitch * this.pitchRate * dt);
    this.mesh.quaternion.multiply(_qPitch);

    // Yaw (ramped keyboard + mouse)
    let targetYaw = 0;
    if (input.left) targetYaw += 1;
    if (input.right) targetYaw -= 1;
    targetYaw -= input.mouseX * MOUSE_SENSITIVITY;
    targetYaw = Math.max(-1, Math.min(1, targetYaw));
    const yawRamp = 1 - Math.exp(-INPUT_RAMP_SPEED * dt);
    this.currentYaw += (targetYaw - this.currentYaw) * yawRamp;

    _qYaw.setFromAxisAngle(_axisUp, this.currentYaw * this.yawRate * dt);
    this.mesh.quaternion.multiply(_qYaw);
    this.mesh.quaternion.normalize();

    // Auto-level: dampen accumulated roll back to zero when not barrel rolling
    if (this.rollRemaining <= 0) {
      _euler.setFromQuaternion(this.mesh.quaternion, 'YXZ');
      if (Math.abs(_euler.z) > 0.001) {
        const t = 1 - Math.exp(-AUTO_LEVEL_SPEED * dt);
        _euler.z *= 1 - t;
        this.mesh.quaternion.setFromEuler(_euler);
      }
    }

    // Roll (longitudinal axis) — full 360° on keypress + lateral shift
    if (this.rollCooldown > 0) this.rollCooldown -= dt;
    if (this.flareCooldownTimer > 0) this.flareCooldownTimer -= dt;
    this.flareJustFired = false;
    this.rollJustStarted = false;
    if (this.rollRemaining <= 0 && this.rollCooldown <= 0) {
      if (input.flare) {        // Q = counter-clockwise + shift left
        this.rollRemaining = Math.PI * 2;
        this.rollJustStarted = true;
        this.rollDir = -1;
        this.rollCooldown = ROLL_DURATION + 0.5;
        this.rollRight.set(1, 0, 0).applyQuaternion(this.mesh.quaternion);
      } else if (input.boost) { // E = clockwise + shift right
        this.rollRemaining = Math.PI * 2;
        this.rollJustStarted = true;
        this.rollDir = 1;
        this.rollCooldown = ROLL_DURATION + 0.5;
        this.rollRight.set(1, 0, 0).applyQuaternion(this.mesh.quaternion);
      }
    }
    if (this.rollRemaining > 0) {
      const step = Math.min(ROLL_SPEED * dt, this.rollRemaining);
      this.rollRemaining -= step;
      _qRoll.setFromAxisAngle(_axisForward, this.rollDir * step);
      this.mesh.quaternion.multiply(_qRoll);
      this.mesh.quaternion.normalize();

      // Shift laterally using the fixed direction captured at roll start
      const lateralSpeed = ROLL_LATERAL_DISTANCE / ROLL_DURATION;
      this.mesh.position.addScaledVector(this.rollRight, this.rollDir * lateralSpeed * dt);
    }

    // Flare (X key, drone only)
    if (input.deployFlare && this.role === 'shahed' && this.flareUses > 0 && this.flareCooldownTimer <= 0) {
      this.flareUses--;
      this.flareCooldownTimer = 8;
      this.flareJustFired = true;
      console.log(`[SKYFALL] Flare deployed! ${this.flareUses} remaining`);
    }

    // Visual bank
    const targetBank = this.currentYaw * BANK_ANGLE;
    this.visualBank += (targetBank - this.visualBank) * BANK_SPEED * dt;

    _qBank.setFromAxisAngle(_bankAxis, this.visualBank);
    this.mesh.quaternion.multiply(_qBank);
    this.mesh.quaternion.normalize();

    // ── Move forward ──────────────────────────────────────
    _forward.set(0, 0, -1).applyQuaternion(this.mesh.quaternion);
    this.mesh.position.addScaledVector(_forward, this.speed * dt);

    // Cache velocity for network emission
    this._velocity.copy(_forward).multiplyScalar(this.speed);
  }

  get velocity(): { x: number; y: number; z: number } {
    return { x: this._velocity.x, y: this._velocity.y, z: this._velocity.z };
  }

  resetOnRespawn(): void {
    this.flareUses = 3;
    this.flareCooldownTimer = 0;
    this.boostFuel = BOOST_MAX_DURATION;
    this.boostRefillTimer = 0;
    this.gunHeat = 0;
    this.gunCooldownTimer = 0;
    this.rollRemaining = 0;
    this.rollCooldown = 0;
  }

  get boostFraction(): number {
    return this.boostFuel / BOOST_MAX_DURATION;
  }

  get isBoosting(): boolean {
    return this.boosting;
  }

  get gunHeatFraction(): number {
    return this.gunHeat / GUN_MAX_HEAT;
  }

  get isFiring(): boolean {
    return this.firing;
  }

  get didBarrelRoll(): boolean {
    return this.rollJustStarted;
  }

  get didFlare(): boolean {
    return this.flareJustFired;
  }

  get flaresRemaining(): number {
    return this.flareUses;
  }

  get maxFlares(): number {
    return 3;
  }
}
